import React, { useState } from 'react';

import { Icon } from 'bsa-ui';
import cn from 'classnames';
import memoize from 'memoize-one';
import { MdContentPaste, MdInfo } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { organizationGuidSel, unitTypeIdSel } from '@context';
import { RosterScout } from '@modules/advancement/duck/types';
import { reportCodes } from '@modules/reports/basicReports/constants';
import {
  NormalizedReportItem,
  getContextToken,
  getCustomReportCode,
  getCustomReportUrl,
} from '@modules/reports/basicReports/duck/utils';
import { CustomReports } from '@modules/reports/constants';
import { useLazyGetReportListByOrgQuery } from '@modules/rtk/webscript-api';
import { ROUTE_REPORTS } from '@modules/shared';
import { toPurchaseCountSel } from '@modules/userNotifications/duck/selectors';
import { openReportWithQueryParams } from '@modules/utils/openBasicReport';
import { OfflineTooltip } from '@offline';
import {
  Badge,
  Button,
  Dropdown,
  ExpandToggle,
  Menu,
  S,
  Spin,
} from '@shared/components';
import { isScoutbookRoleSel } from '@shared/duck/selectors/general.selectors';
import { canRecordProgressForSelectedItemsSel } from '@shared/duck/selectors/roster.selectors';

import { openAdvancementReportModal } from '../../../../advancementReport';
import { openReportParamsModal, reportsSel } from '../../../../reports';
import { reportTypes } from '../../../constants';
import { openUnitReportModal, setUnitRosterReportLoading } from '../../actions';
import {
  notAwardedAdvancementsCountSel,
  reportsSelectedItemsSel,
  selectedYouthSel,
} from '../../selectors';
import styles from './RunReportDropdown.less';

const advancementReportSelectedPeople = 'advancementReportSelectedPeople';
const navigateToReports = 'navigateToReports';
const basicReport = 'basicReport';

const MenuItem = Menu.Item;
const MenuDivider = Menu.Divider;

interface RunReportDropdownProps {
  disabled: boolean;
  showAllUsers: boolean;
}

const RunReportDropdown: React.FC<RunReportDropdownProps> = ({
  disabled,
  showAllUsers,
}) => {
  const [visible, setVisible] = useState(false);
  const [getReportList, { isLoading }] = useLazyGetReportListByOrgQuery();

  const organizationGuid = useSelector(organizationGuidSel);
  const programId = useSelector(unitTypeIdSel);
  const selectedItems = useSelector(reportsSelectedItemsSel);
  const selectedYouthCount = useSelector(selectedYouthSel).length;
  const notAwardedAdvancementsCount = useSelector(
    notAwardedAdvancementsCountSel,
  );
  const allNotAwardedAdvancementsCount = useSelector(toPurchaseCountSel);
  const unitReports = useSelector(reportsSel);
  const isScoutbookRole = useSelector(isScoutbookRoleSel);
  const canRecordAdvancement = useSelector(
    canRecordProgressForSelectedItemsSel,
  );

  const dispatch = useDispatch();

  const onOpenAdvancementReportModal = ({
    showAllUsers,
  }: {
    showAllUsers: boolean;
  }) => {
    dispatch(openAdvancementReportModal({ showAllUsers }));
  };

  const onOpenReportModal = (payload: {
    organizationGuid: string;
    users: RosterScout[];
    reportType: string;
    reportCode?: string;
    report?: NormalizedReportItem;
  }) => {
    dispatch(openReportParamsModal(payload));
  };

  const onNavigateToReports = () => {
    dispatch({ type: ROUTE_REPORTS });
  };

  const onSetUnitRosterReportLoading = (isLoading: boolean) => {
    dispatch(setUnitRosterReportLoading(isLoading));
  };

  const onOpenUnitReportModal = (reportCode: string) => {
    dispatch(openUnitReportModal(reportCode));
  };

  const handleVisibleChange = (visible: boolean) => {
    if (!unitReports.length && selectedYouthCount > 0) {
      getReportList({ params: { OrganizationGUID: organizationGuid } });
    }
    setVisible(visible);
  };

  const handleMenuClick = async ({ key }: { key: string }) => {
    setVisible(false);

    switch (key) {
      case CustomReports.AdvancementReport: {
        return onOpenAdvancementReportModal({
          showAllUsers,
        });
      }
      case CustomReports.AdvancementHistory: {
        return onOpenReportModal({
          organizationGuid,
          users: selectedItems,
          reportType: CustomReports.AdvancementHistory,
          reportCode: reportCodes[CustomReports.AdvancementHistory],
        });
      }
      case CustomReports.ActivitiesSummary: {
        let reportsInfo = unitReports;
        if (!reportsInfo.length) {
          const { reports } = await getReportList({
            params: { OrganizationGUID: organizationGuid },
          }).unwrap();
          reportsInfo = reports;
        }
        const report = reportsInfo.find(
          report => report.reportCode === reportCodes.ACTIVITIES_SUMMARY,
        );
        return onOpenReportModal({
          organizationGuid,
          users: selectedItems,
          reportType: reportTypes.ACTIVITIES_SUMMARY,
          report,
        });
      }
      case CustomReports.UnitRoster: {
        onSetUnitRosterReportLoading(true);
        const personGuids = selectedItems
          .map(item => item.personGuid)
          .filter(Boolean) as string[];

        const params: {
          organizationGuid: string;
          ProgramID: number;
          ContextToken?: string;
        } = {
          organizationGuid,
          ProgramID: programId,
        };

        if (personGuids.length) {
          const response = await getContextToken(
            organizationGuid,
            personGuids,
            reportCodes.UnitRosterReport,
          );
          params.ContextToken = response.ContextToken;
        }

        openReportWithQueryParams(
          getCustomReportUrl(getCustomReportCode(CustomReports.UnitRoster)),
          params,
        );
        onSetUnitRosterReportLoading(false);
        return;
      }
      case navigateToReports: {
        return onNavigateToReports();
      }
      case advancementReportSelectedPeople: {
        return;
      }

      default: {
        if (key.includes(basicReport)) {
          const reportCode = key.split('_')[1];
          onOpenUnitReportModal(reportCode);
          return;
        }
        throw new Error(`Unknown report type: ${key}`);
      }
    }
  };

  const renderUnitReports = memoize(
    (
      unitReports: NormalizedReportItem[],
      isLoading: boolean,
      hasSelectedYouth: boolean,
    ) => {
      if (isLoading) {
        return [
          <MenuDivider key="div_loadingReportsDivider" />,
          <MenuItem key="div_loadingReports" disabled>
            <div className={styles.loadingIndicator}>
              <Spin
                className={styles.loadingIndicatorWrapper}
                indicator={
                  <Icon type="loading" spin style={{ fontSize: 12 }} />
                }
                size="small"
              />
              <S size="5" semibold>
                <FormattedMessage id="shared.loading" />
              </S>
            </div>
          </MenuItem>,
        ];
      }

      return unitReports.map(({ ReportName, reportCode }) => [
        <MenuDivider key={`div_${reportCode}`} />,
        <MenuItem
          key={`${basicReport}_${reportCode}`}
          disabled={!hasSelectedYouth}
        >
          {ReportName}{' '}
          {!hasSelectedYouth && (
            <FormattedMessage id="packRoster.RunReportDropdown.selectYouth" />
          )}
        </MenuItem>,
      ]);
    },
  );

  const selectedCount = selectedItems.length;
  const hasSelectedPeople = selectedCount > 0;
  const readyToBeAwardedAdvancements = hasSelectedPeople
    ? notAwardedAdvancementsCount
    : allNotAwardedAdvancementsCount;

  const buttons = (
    <Menu onClick={handleMenuClick}>
      {!!hasSelectedPeople && [
        // Menu items don't work properly inside React.Fragments
        // https://github.com/ant-design/ant-design/issues/10688
        <MenuItem
          key={advancementReportSelectedPeople}
          className={styles.selectedPeopleMenuItem}
        >
          <span className={styles.selectedPeople}>
            <MdInfo className={styles.infoIcon} />
            <FormattedMessage id="packRoster.RunReportDropdown.selectedPeople" />
          </span>
        </MenuItem>,
        <MenuDivider key="div1" />,
      ]}
      {!!(!isScoutbookRole || canRecordAdvancement) && [
        <MenuItem
          key={CustomReports.AdvancementReport}
          id="qa_advancementReport"
          className={styles.advancementReport}
        >
          <FormattedMessage id="packRoster.RunReportDropdown.scoutShopReport" />
          <Badge
            count={readyToBeAwardedAdvancements}
            className={styles.notAwardedBadge}
          />
        </MenuItem>,
        <MenuDivider key="divider-reports" />,
      ]}
      <MenuItem key={CustomReports.AdvancementHistory} id="advancementSummary">
        <FormattedMessage id="packRoster.RunReportDropdown.advancementHistory" />
      </MenuItem>
      <MenuDivider />
      {[
        <MenuItem key={CustomReports.ActivitiesSummary} id="activitiesSummary">
          <div className={styles.dropdownMenuItem}>
            <FormattedMessage id="packRoster.RunReportDropdown.activitiesSummary" />
          </div>
        </MenuItem>,
        <MenuDivider key="div3" />,
      ]}
      <MenuItem key={CustomReports.UnitRoster} id="qa_unitRoster">
        <FormattedMessage id="packRoster.RunReportDropdown.unitRoster" />
      </MenuItem>
      {[
        ...renderUnitReports(unitReports, isLoading, selectedYouthCount > 0),
        <MenuDivider key="div2" />,
        <MenuItem key={navigateToReports}>
          <FormattedMessage id="packRoster.RunReportDropdown.more" />
        </MenuItem>,
      ]}
    </Menu>
  );

  return (
    <OfflineTooltip placement="top">
      <Dropdown
        overlay={buttons}
        trigger={['click']}
        placement="bottomRight"
        visible={visible}
        disabled={disabled}
        onVisibleChange={handleVisibleChange}
      >
        <Button
          id="qa_runReport"
          className={cn(
            'Joyride__RosterRunReportButton',
            {
              [styles.disabled]: disabled,
            },
            styles.toggleButton,
          )}
          loading={isLoading}
          ghost
          noBorder
          color="white"
          size="small"
          uppercase={false}
        >
          <MdContentPaste className={styles.reportIcon} />
          <FormattedMessage id="packRoster.RunReportDropdown.runReport" />
          <Badge
            count={readyToBeAwardedAdvancements}
            className={styles.reportBadge}
          />
          <ExpandToggle
            className={styles.arrow}
            // eslint-disable-next-line react/jsx-no-leaked-render
            expanded={visible && !disabled}
          />
        </Button>
      </Dropdown>
    </OfflineTooltip>
  );
};

export default RunReportDropdown;
